import { memo, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { IconCheck, IconHelpCircle } from '@u21/tabler-icons';

import { U21Button } from 'app/shared/u21-ui/components/input/U21Button';
import { U21Checkbox } from 'app/shared/u21-ui/components/input/U21Checkbox';
import { U21MultiSelect } from 'app/shared/u21-ui/components/input/select/U21MultiSelect';
import { U21TextField } from 'app/shared/u21-ui/components/input/text-field/U21TextField';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import { alpha } from '@mui/system/colorManipulator';

const MAX_CHECKBOX_CHOICES = 5;

export interface U21AiChatMultiSelectBubbleProps {
  question: string;
  choices: string[];
  disabled?: boolean;
  readonly?: boolean;
  values?: string[];
  onSubmit: (answer: string[]) => void;
}

export const U21AiChatMultiSelectBubble = memo(
  ({
    question,
    choices,
    disabled,
    readonly,
    values,
    onSubmit,
  }: U21AiChatMultiSelectBubbleProps) => {
    const valuesSet = useMemo(() => new Set(values), [values]);
    const choiceSet = useMemo(() => new Set(choices), [choices]);

    // Note: We should always only have a single text input value since we only have a single text input
    // Fallback for having multiple text input values is to join the values with a comma
    const textInputValue = useMemo(
      () => [...valuesSet.difference(choiceSet)].join(', '),
      [valuesSet, choiceSet],
    );

    const [selected, setSelected] = useState<string[]>(() => [
      ...valuesSet.intersection(choiceSet),
    ]);
    const [customText, setCustomText] = useState(textInputValue);

    const handleToggle = useCallback((choice: string) => {
      setSelected((prev) =>
        prev.includes(choice)
          ? prev.filter((c) => c !== choice)
          : [...prev, choice],
      );
    }, []);

    const handleSubmit = useCallback(() => {
      const allValues = [
        ...selected,
        ...(customText.trim() ? [customText.trim()] : []),
      ];
      if (allValues.length > 0) {
        onSubmit?.(allValues);
      }
    }, [selected, customText, onSubmit]);

    const hasValue = useMemo(
      () => selected.length > 0 || customText.trim().length > 0,
      [selected, customText],
    );

    if (readonly) {
      const selectedValues = values ?? [];
      return (
        <BubbleCard>
          <U21Spacer spacing={1.5}>
            <U21Spacer horizontal align="start">
              <StyledIconHelpCircle size={18} $greyed />
              <U21Typography variant="subtitle2" color="text.secondary">
                {question}
              </U21Typography>
            </U21Spacer>

            {selectedValues.length > 0 && (
              <U21Spacer horizontal align="start">
                <StyledIconCheck />
                <U21Typography variant="subtitle2">
                  {selectedValues.length > 1
                    ? `${selectedValues.slice(0, -1).join(', ')}, and ${selectedValues[selectedValues.length - 1]}`
                    : selectedValues[0]}
                </U21Typography>
              </U21Spacer>
            )}
          </U21Spacer>
        </BubbleCard>
      );
    }

    return (
      <BubbleCard>
        <U21Spacer spacing={1.5}>
          <U21Spacer horizontal align="start">
            <StyledIconHelpCircle size={18} />
            <U21Typography variant="subtitle2">{question}</U21Typography>
          </U21Spacer>
          {choices.length > MAX_CHECKBOX_CHOICES ? (
            <U21MultiSelect
              disabled={disabled}
              value={selected}
              onChange={(value) => setSelected(value as string[])}
              options={choices.map((choice) => ({
                value: choice,
                text: choice,
              }))}
              placeholder="Select options..."
            />
          ) : (
            <U21Spacer spacing={1}>
              {choices.map((choice) => {
                const isSelected = selected.includes(choice);
                return (
                  <ChoiceRow
                    horizontal
                    key={choice}
                    $selected={isSelected}
                    $disabled={disabled}
                    onClick={disabled ? undefined : () => handleToggle(choice)}
                  >
                    <U21Checkbox
                      checked={isSelected}
                      disabled={disabled}
                      onChange={(_, e) => {
                        e.stopPropagation();
                        handleToggle(choice);
                      }}
                    />
                    <U21Typography variant="body2" color="text.primary">
                      {choice}
                    </U21Typography>
                  </ChoiceRow>
                );
              })}
            </U21Spacer>
          )}
          <U21TextField
            type="textarea"
            disabled={disabled}
            value={customText}
            onChange={(value) => setCustomText(String(value ?? ''))}
            placeholder="Or add something else..."
            minRows={1}
          />
          <StyledSubmitButton
            color="primary"
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={disabled || !hasValue}
          >
            Submit
          </StyledSubmitButton>
        </U21Spacer>
      </BubbleCard>
    );
  },
);

const BubbleCard = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-left: 4px solid ${({ theme }) => theme.palette.primary.main};
  padding: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 0 ${({ theme }) => theme.shape.borderRadius}px
    ${({ theme }) => theme.shape.borderRadius}px 0;
`;

const ChoiceRow = styled(U21Spacer)<{
  $selected: boolean;
  $disabled?: boolean;
}>`
  align-items: flex-start;
  padding: ${({ theme }) => theme.spacing(1)};
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.palette.primary.main : theme.palette.divider};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  background: ${({ theme, $selected, $disabled }) => {
    if ($disabled) {
      return theme.palette.action.disabledBackground;
    }

    if ($selected) {
      return alpha(theme.palette.primary.main, 0.08);
    }

    return theme.palette.background.paper;
  }};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  transition:
    border-color 0.15s,
    background-color 0.15s;

  &:hover {
    border-color: ${({ theme, $disabled }) =>
      $disabled ? undefined : theme.palette.primary.main};
  }

  .MuiCheckbox-root {
    padding: 0;
  }
`;

const StyledIconHelpCircle = styled(IconHelpCircle)<{ $greyed?: boolean }>`
  color: ${({ theme, $greyed }) =>
    $greyed ? theme.palette.text.secondary : theme.palette.info.main};
  flex-shrink: 0;
`;

const StyledIconCheck = styled(IconCheck)`
  color: ${({ theme }) => theme.palette.primary.main};
  width: 18px;
  height: 18px;
  flex-shrink: 0;
`;

const StyledSubmitButton = styled(U21Button)`
  width: fit-content;
`;
